<template>
  <div class="chart-wrapper">
    <LineChartComponent 
      :chart-data="chartData" 
      :chart-options="mergedOptions" 
    />
  </div>
</template>

<script>
import { Line } from 'vue-chartjs'
import { 
  Chart as ChartJS, 
  Title, 
  Tooltip, 
  Legend, 
  LineElement, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  Filler 
} from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, LineElement, CategoryScale, LinearScale, PointElement, Filler)

export default {
  name: 'LineChart',
  components: { 
    LineChartComponent: Line  // ✅ Renombrar para evitar conflicto con elemento HTML
  },
  props: {
    labels: {
      type: Array,
      default: () => []
    },
    data: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: 'Dataset'
    },
    borderColor: {
      type: String,
      default: '#6b20c1'
    },
    backgroundColor: {
      type: String,
      default: 'rgba(107, 32, 193, 0.1)'
    },
    fill: {
      type: Boolean,
      default: false
    },
    tension: {
      type: Number,
      default: 0.4
    },
    title: {
      type: String,
      default: ''
    },
    chartOptions: {
      type: Object,
      default: () => ({})
    },
    // Soporte para múltiples datasets
    datasets: {
      type: Array,
      default: null
    }
  },
  computed: {
    chartData() {
      // Si se proporcionan datasets personalizados, usarlos
      if (this.datasets && this.datasets.length > 0) {
        return {
          labels: this.labels,
          datasets: this.datasets.map(dataset => ({
            label: dataset.label || 'Dataset',
            borderColor: dataset.borderColor || this.borderColor,
            backgroundColor: dataset.backgroundColor || this.backgroundColor,
            data: dataset.data || [],
            tension: dataset.tension ?? this.tension,
            fill: dataset.fill ?? this.fill,
            borderWidth: dataset.borderWidth || 2,
            pointRadius: dataset.pointRadius ?? 3,
            pointHoverRadius: dataset.pointHoverRadius ?? 5
          }))
        }
      }
      
      // Dataset simple
      return {
        labels: this.labels,
        datasets: [{
          label: this.label,
          borderColor: this.borderColor,
          backgroundColor: this.backgroundColor,
          data: this.data,
          tension: this.tension,
          fill: this.fill,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5
        }]
      }
    },
    mergedOptions() {
      const defaultOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          title: {
            display: this.title !== '',
            text: this.title
          }
        },
        scales: {
          y: {
            beginAtZero: true
          }
        }
      }
      
      return this.deepMerge(defaultOptions, this.chartOptions)
    }
  },
  methods: {
    deepMerge(target, source) {
      const output = Object.assign({}, target)
      if (this.isObject(target) && this.isObject(source)) {
        Object.keys(source).forEach(key => {
          if (this.isObject(source[key])) {
            if (!(key in target)) {
              Object.assign(output, { [key]: source[key] })
            } else {
              output[key] = this.deepMerge(target[key], source[key])
            }
          } else {
            Object.assign(output, { [key]: source[key] })
          }
        })
      }
      return output
    },
    isObject(item) {
      return item && typeof item === 'object' && !Array.isArray(item)
    }
  }
}
</script>

<style scoped>
.chart-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}
</style>